import { message } from "antd";
import { useDispatch } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setLoaderState } from "../../redux/slices/loaderSlice";
import useGetData from "../../api/useGetData";
import usePostData from "../../api/usePostData";
import axios from "axios";

/**
 * A helper hook that gathers the verification of the different paymment methods together in one place.
 *
 * @param {Object} props
 * @param {Function} props.setPaymentStatus - A set state function that sets the payment status state
 * @returns
 */

const useHandlePaymentVerification = ({ setPaymentStatus }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {t} = useTranslation("global");
  const { postData: createBooking } = usePostData();
  const [searchParams] = useSearchParams();
  const { getData: getPayByLinkStatus } = useGetData({ route: "api/method/airport_transport.api.integrations.paybylink.get_payment_status", notLoadData: true, });
  const { getData: getTripDetails } = useGetData({ route: "api/method/airport_transport.api.integrations.hyperpay.get_trip_details", notLoadData: true, });
  const hyperpayId = searchParams.get("id");

 const checkCardPayment = async (checkoutId, bookingData) => {
  dispatch(setLoaderState(true));
  try {
    // Step 1: Create booking
    const bookingResponse = await createBooking({
      route: "api/method/airport_transport.api.bookings.user_booking",
      data: {
        ...bookingData,
        hyperpay_id: hyperpayId,
      },
    });
    console.log("Booking Response:", bookingResponse);

    if (bookingResponse?.error) {
      if(bookingResponse?.error === "ValidationError('You already have a trip for this date and time')") {
        message.error(t("payment_booked_before_text"));
        setPaymentStatus("booked_before");
      }
      else{
        message.error(bookingResponse?.error);
        setPaymentStatus("failed");
      }
      return;
    }

    if (bookingResponse.status !== 200) {
      setPaymentStatus("failed");
      navigate("/");
      return;
    }

    // Step 2: Fetch trip details
    const tripRes = await getTripDetails({ params: { id: checkoutId } });

    if (tripRes.status === 200) {
      const {
        rider, location, city, arrival_date, arrival_time, vehicle_type,
        ride_discount, price, ride_proposal, zone, pick_up, drop_off,
        language, destination_name, payment_method, booking_hours,
      } = bookingResponse.data.data;

      const newBookingData = {
        checkoutId: hyperpayId,
        rider, location, city, arrival_date, arrival_time, vehicle_type,
        ride_discount, price, ride_proposal, zone, pick_up, drop_off,
        language, destination_name, payment_method, booking_hours,
      };

      sessionStorage.setItem("bookingData", JSON.stringify(newBookingData));

      const tripPaymentStatus = tripRes.data.data.payment_status;
      if (tripPaymentStatus !== "Not Paid") {
        setPaymentStatus("success");
        return;
      }
    }

    // Step 3: Validate payment
    let entityId;
    switch (bookingData.payment_method) {
      case "Mada":
        entityId = process.env.REACT_APP_MADA_ENTRY_ID;
        break;
      case "Credit Card":
        entityId = process.env.REACT_APP_CREDIT_CARD_ENTRY_ID;
        break;
      case "Apple Pay":
        entityId = process.env.REACT_APP_APPLE_PAY_ENTRY_ID;
        break;
      default:
        message.error("Unsupported payment method");
        setPaymentStatus("failed");
        navigate("/");
        return;
    }
    const url = `${process.env.REACT_APP_PAYMENT_GATEWAY_BASE_URL}/checkouts/${checkoutId}/payment?entityId=${entityId}`;
    const options = {
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_CREDIT_CARD_AUTH_TOKEN}`,
      },
    };

    const paymentRes = await axios.get(url, options);
    console.log("Payment Response:", paymentRes);

    const resultCode = paymentRes?.data?.result?.code;
    if (resultCode === "000.000.000") {
      setPaymentStatus("success");
    } else if (resultCode === "100.380.401") {
      setPaymentStatus("failed");
    } else {
      setPaymentStatus("failed");
    }

  } catch (error) {
    console.error("Payment Error:", error);
    message.error(error?.response?.data?.result?.description || "Payment failed");
    setPaymentStatus("failed");
    navigate("/");
  } finally {
    dispatch(setLoaderState(false));
  }
};

  
  /**
   * A function that is used to check the payment status for the pay by link payment.
   * It sends request directly to the payment gateway to check whether it was successfull or not, and if it was so, it sends a request to the backend to create a booking
   *
   * @param {Object} props
   * @param {string} props.id
   * @param {string} props.checkoutId
   */
  const checkPayByLink = async ({ checkoutId, id }) => {
    const response = await getPayByLinkStatus({
      params: {
        checkoutId,
        id,
      },
    });
    if (response.status === 200) {
      setPaymentStatus("success");
    } else {
      setPaymentStatus("failed");
    }
  };
  return { checkCardPayment, checkPayByLink };
};

export default useHandlePaymentVerification;